import { useEffect } from "react"
import { Portal } from "./portal"

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  subtitle?: string
  children: React.ReactNode
  maxWidth?: "sm" | "md" | "lg" | "xl"
}

const MAX_WIDTH_CLASSES = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
}

export function Modal({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  maxWidth = "lg",
}: ModalProps) {
  useEffect(() => {
    if (!isOpen) return

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose()
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [isOpen, onClose])

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <Portal>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-ink/30 backdrop-blur-sm"
        onClick={handleBackdropClick}
      >
        <div
          className={`max-h-[90vh] w-full ${MAX_WIDTH_CLASSES[maxWidth]} overflow-y-auto rounded-[20px] border border-gold-500 bg-white p-6 shadow-xl`}
        >
          <div className="mb-6">
            <h2 className="font-display text-xl font-bold text-ink">{title}</h2>
            {subtitle && <p className="mt-1 text-sm text-ink/60">{subtitle}</p>}
          </div>
          {children}
        </div>
      </div>
    </Portal>
  )
}

interface ModalActionsProps {
  children: React.ReactNode
  align?: "start" | "end"
}

export function ModalActions({ children, align = "end" }: ModalActionsProps) {
  return (
    <div className={`flex gap-3 pt-4 ${align === "start" ? "justify-start" : "justify-end"}`}>
      {children}
    </div>
  )
}

interface ModalButtonProps {
  variant?: "primary" | "secondary" | "danger"
  type?: "button" | "submit"
  disabled?: boolean
  onClick?: () => void
  children: React.ReactNode
}

export function ModalButton({
  variant = "secondary",
  type = "button",
  disabled = false,
  onClick,
  children,
}: ModalButtonProps) {
  const variantClasses = {
    primary: "bg-forest-950 text-white hover:bg-forest-900",
    secondary: "border border-ink/20 text-ink hover:bg-cream-100",
    danger: "border border-red-200 text-red-600 hover:bg-red-50",
  }

  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      className={`rounded-full px-5 py-2.5 text-sm font-medium transition-colors disabled:opacity-50 ${variantClasses[variant]}`}
    >
      {children}
    </button>
  )
}
